import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * This hook is used to manage the expanded project data in the URL.
 * It is used to store the expanded project data in the URL and to
 * add and remove the expanded project data from the URL.
 *
 * @returns An object with the expanded project data,
 * a function to add expanded project data,
 * a function to remove expanded project data,
 * and a function to set expanded project data.
 */
export const useExpandParam = () => {
  const [_, setSearchParams] = useSearchParams();
  const [projectNodesMapping, setProjectNodesMapping] = useState<
    Record<number, string[]>
  >({});

  /**
   * This data store all expanded projects and their parents.
   * There could be parents that are NOT in the DAG right now, but there is a relationship to them.
   */
  const expandedProjectData = useMemo(() => {
    return Object.entries(projectNodesMapping).reduce(
      (acc, [projectId, parents]) => {
        acc[Number(projectId)] = parents;
        return acc;
      },
      {} as Record<number, string[]>,
    );
  }, [projectNodesMapping]);

  /**
   * This function is used to add a node to the expanded project data.
   * It modifies the state and the URL.
   *
   * @param projectId - The project id to add.
   * @param parents - The parents to add.
   */
  const addExpandedProjectData = (projectId: number, parents: string[]) => {
    setProjectNodesMapping((prev) => ({
      ...prev,
      [projectId]: parents,
    }));
    setSearchParams((params) => {
      const expandParam = params.get('expand');
      const expandParamSet = new Set([
        ...(expandParam?.split(',').map(Number) || []),
        projectId,
      ]);
      params.set('expand', Array.from(expandParamSet).join(','));
      return params;
    });
  };

  /**
   * This function is used to remove a node from the expanded project data.
   * it modifies the state and the URL if the project has no more assets.
   * If the parentId is not provided, it will remove the project from the expanded project data.
   *
   * @param projectId - The project id to remove.
   * @param parentId - The parent id to remove.
   * @param visibleParents - The visible parents of this project.
   */
  const removeExpandedProjectData = (
    projectId: number,
    parentId?: string,
    visibleParents?: string[],
  ) => {
    setProjectNodesMapping((prev) => {
      const newMapping = { ...prev };
      if (!parentId) {
        delete newMapping[projectId];
        return newMapping;
      }

      // If the project has only one parent and it is the parent we are removing, we can remove the project
      if (
        (visibleParents?.length === 1 || newMapping[projectId]?.length === 1) &&
        newMapping[projectId]?.includes(parentId)
      ) {
        delete newMapping[projectId];
        setSearchParams((params) => {
          const expandParam = params.get('expand');
          const expandParamArray = expandParam?.split(',').map(Number) || [];
          const newArray = expandParamArray.filter((id) => id !== projectId);
          if (newArray.length > 0) {
            params.set('expand', newArray.join(','));
          } else {
            params.delete('expand');
          }
          return params;
        });
      } else {
        newMapping[projectId] = newMapping[projectId].filter((id) => id !== parentId);
      }
      return newMapping;
    });
  };

  /**
   * This function is used to set the expanded project data
   * It does not modify the URL, it only sets the state.
   *
   * @param projectNodesMapping - The expanded project data to set.
   */
  const setExpandedProjectData = (projectNodesMapping: Record<number, string[]>) => {
    setProjectNodesMapping(projectNodesMapping);
  };

  return {
    expandedProjectData,
    addExpandedProjectData,
    removeExpandedProjectData,
    setExpandedProjectData,
  };
};
